import { Globe, Sparkles, Mail, Linkedin, Youtube, Instagram, Calendar, MapPin, ArrowUpRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { Reveal } from '@/components/landing/Reveal';

/**
 * EVENTS
 * To add or remove an event, edit the `events` array below.
 * - `date`: ISO 8601 (YYYY-MM-DD). Past events are auto-hidden.
 * - `title`, `location`, `href` are required.
 * - Sorted automatically by date ascending.
 */
type EventItem = {
  date: string;
  title: string;
  location: string;
  href: string;
};

const events: EventItem[] = [
  {
    date: '2026-04-29',
    title: 'Microsoft AI Tour',
    location: 'Messe Zürich',
    href: 'https://aitour.microsoft.com/flow/microsoft/zurich26/landingpage/page/cityhome',
  },
];

type LinkRow = {
  label: string;
  sublabel?: string;
  href: string;
  Icon: LucideIcon;
  external?: boolean;
};

const primaryLinks: LinkRow[] = [
  { label: 'Website', sublabel: 'lorisscandurra.com', href: 'https://lorisscandurra.com', Icon: Globe, external: true },
  { label: 'Kontakt', sublabel: 'hello@lorisscandurra.com', href: 'mailto:hello@lorisscandurra.com', Icon: Mail, external: true },
];

const appLinks: LinkRow[] = [
  { label: 'KI Use-Case Analyser', sublabel: 'Use Case in 2 Minuten analysieren', href: 'https://lorisscandurra.com/ki-use-case-helper', Icon: Sparkles, external: true },
];

const socialLinks: LinkRow[] = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/scandurra', Icon: Linkedin, external: true },
  { label: 'YouTube', href: 'https://www.youtube.com/@lorisscandurra', Icon: Youtube, external: true },
  { label: 'Instagram', href: 'https://www.instagram.com/lorisscandurra', Icon: Instagram, external: true },
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' });

const LinkCard = ({ row }: { row: LinkRow }) => {
  const className =
    'group flex items-center gap-4 bg-charcoal hover:bg-charcoal/80 p-5 rounded-[2px] border-l-2 border-transparent hover:border-signal transition-all duration-150';
  const content = (
    <>
      <row.Icon className="w-5 h-5 text-cotton shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="text-cotton font-medium text-[15px] leading-tight">{row.label}</p>
        {row.sublabel && (
          <p className="text-cotton/60 text-[13px] mt-0.5 truncate">{row.sublabel}</p>
        )}
      </div>
      <ArrowUpRight
        className="w-4 h-4 text-cotton/50 group-hover:text-signal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-150 shrink-0"
        strokeWidth={1.5}
        aria-hidden="true"
      />
    </>
  );

  if (row.external || row.href.startsWith('mailto:')) {
    return (
      <a href={row.href} target={row.href.startsWith('mailto:') ? undefined : '_blank'} rel="noopener noreferrer" className={className}>
        {content}
      </a>
    );
  }
  return (
    <Link to={row.href} className={className}>
      {content}
    </Link>
  );
};

const Links = () => {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events
    .filter((e) => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <>
      <SEOHead
        title="Loris Scandurra. KI-Berater & Trainer, Baden"
        description="Alle Kanäle, Events und Ressourcen von Loris Scandurra auf einen Blick."
        url="https://lorisscandurra.com"
        canonical="https://lorisscandurra.com"
      />
      <main className="min-h-screen bg-ink">
        <div className="max-w-[480px] mx-auto px-6 py-16 md:py-24">
          {/* Header */}
          <Reveal>
            <div className="flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-stone mb-6">
                <img
                  src="/lovable-uploads/loris-scandurra.png"
                  alt="Loris Scandurra"
                  className="w-full h-full object-cover object-[30%_20%] scale-110"
                  width={96}
                  height={96}
                />
              </div>
              <h1 className="text-cotton font-semibold text-[24px] tracking-[-0.01em]">
                Loris Scandurra
              </h1>
              <p className="editorial-label mt-3">KI-Berater & Trainer · Baden</p>
              <span className="editorial-line mt-6" aria-hidden="true" />
            </div>
          </Reveal>

          {/* Primary links */}
          <section className="mt-10 space-y-3" aria-label="Primäre Links">
            {primaryLinks.map((row, i) => (
              <Reveal key={row.label} delay={i * 60}>
                <LinkCard row={row} />
              </Reveal>
            ))}
          </section>

          {/* Apps */}
          <section className="mt-12" aria-label="Apps">
            <Reveal>
              <p className="editorial-label mb-4">Apps</p>
            </Reveal>
            <div className="space-y-3">
              {appLinks.map((row, i) => (
                <Reveal key={row.label} delay={i * 60}>
                  <LinkCard row={row} />
                </Reveal>
              ))}
            </div>
          </section>

          {/* Socials */}
          <section className="mt-12" aria-label="Social Media">
            <Reveal>
              <p className="editorial-label mb-4">Folg mir</p>
            </Reveal>
            <div className="space-y-3">
              {socialLinks.map((row, i) => (
                <Reveal key={row.label} delay={i * 60}>
                  <LinkCard row={row} />
                </Reveal>
              ))}
            </div>
          </section>

          {/* Events */}
          <section className="mt-12" aria-label="Events und Termine">
            <Reveal>
              <p className="editorial-label mb-4">Events & Termine</p>
            </Reveal>
            {upcoming.length === 0 ? (
              <Reveal>
                <p className="text-cotton/60 text-[14px] leading-[1.7] bg-charcoal p-5 rounded-[2px]">
                  Aktuell keine öffentlichen Termine geplant.
                </p>
              </Reveal>
            ) : (
              <div className="space-y-3">
                {upcoming.map((ev, i) => (
                  <Reveal key={ev.date + ev.title} delay={i * 60}>
                    <a
                      href={ev.href}
                      target={ev.href.startsWith('http') ? '_blank' : undefined}
                      rel="noopener noreferrer"
                      className="group block bg-charcoal hover:bg-charcoal/80 p-5 rounded-[2px] border-l-2 border-transparent hover:border-signal transition-all duration-150"
                    >
                      <div className="flex items-center gap-2 text-cotton/60 text-[12px] uppercase tracking-[0.08em]">
                        <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                        <time dateTime={ev.date}>{formatDate(ev.date)}</time>
                      </div>
                      <p className="mt-2 text-cotton font-medium text-[16px] leading-snug">{ev.title}</p>
                      <div className="mt-2 flex items-center gap-1.5 text-cotton/60 text-[13px]">
                        <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                        <span>{ev.location}</span>
                      </div>
                      <p className="mt-3 text-signal text-[13px] font-medium group-hover:underline underline-offset-4">
                        Anmelden →
                      </p>
                    </a>
                  </Reveal>
                ))}
              </div>
            )}
          </section>

          {/* Footer */}
          <footer className="mt-16 pt-8 border-t border-charcoal/60 flex flex-col items-center gap-3 text-[12px] text-cotton/50">
            <p>© {new Date().getFullYear()} lorisscandurra</p>
            <div className="flex items-center gap-4">
              <a href="https://lorisscandurra.com/impressum" target="_blank" rel="noopener noreferrer" className="hover:text-cotton transition-colors">Impressum</a>
              <span aria-hidden="true">·</span>
              <a href="https://lorisscandurra.com/datenschutz" target="_blank" rel="noopener noreferrer" className="hover:text-cotton transition-colors">Datenschutz</a>
            </div>
          </footer>
        </div>
      </main>
    </>
  );
};

export default Links;
